import { Query } from '@flakiness/server/common/fql/query.js';
import { type WireTypes } from '@flakiness/server/common/wireTypes.js';
import { Context, ContextProvider, createContext } from '@lit/context';
import { Task, TaskConfig } from '@lit/task';
import { ReactiveElement } from 'lit';

import { EnvGroup } from '@flakiness/server/common/stats/envGroup.js';
import { TestOutcomes } from '@flakiness/server/common/stats/testOutcomes.js';
import { CommitRegistry } from './commitRegistry.js';
import { Linkifier } from './linkifier.js';
import { Router } from './router.js';
export { consume, provide } from '@lit/context';

export const contexts = {
  serverInfo: createContext<WireTypes.ServerInfo>(Symbol('serverInfo')),
  user: createContext<WireTypes.CurrentUser>(Symbol('user')),
  // Superusers only: audit-log entries in the last 24 hours.
  auditLogRecentCount: createContext<number>(Symbol('auditLogRecentCount')),
  dataSnapshotId: createContext<WireTypes.DataSnapshotId>(Symbol('dataSnapshotId')),
  dataSnapshotInfo: createContext<{
    snapshotId?: WireTypes.DataSnapshotId,
    status?: 'current' | 'outdated' | 'missing',
    createdAt?: number,
    // Initial loads replace the project body. Refreshes keep rendering the
    // existing snapshot and report their progress in the project header.
    loadingMode?: 'initial' | 'refresh',
    // The two client-side clocks for the current load. They are never cleared,
    // so any consumer can work out where in the load it is from the pair alone:
    // a `loadedTimestamp` at or after `loadingStartedTimestamp` means the load
    // finished, and anything else means one is in flight. Distinct from
    // `createdAt`, which is when the server built the snapshot.
    loadingStarted?: number,
    loadingFinished?: number,
  }>(Symbol('dataSnapshotInfo')),
  notLoggedIn: createContext<boolean>(Symbol('userNotLoggedIn')),
  org: createContext<WireTypes.Organization>(Symbol('org')),
  billing: createContext<WireTypes.BillingStatus>(Symbol('billing')),
  commitRegistry: createContext<CommitRegistry>(Symbol('commitRegistry')),
  orgMembers: createContext<{ owner: WireTypes.User, members: WireTypes.OrgMember[]}>(Symbol('orgMembers')),
  project: createContext<WireTypes.Project>(Symbol('project')),
  projectPulls: createContext<WireTypes.PagedResponse<WireTypes.PullRequestStats>>(Symbol('projectPulls')),
  testsStatus: createContext<TestOutcomes.TestOutcomeCounts>(Symbol('testsStatus')),
  projectReferences: createContext<{
    branches: WireTypes.PagedResponse<WireTypes.Ref>,
    defaultBranch: WireTypes.Ref,
  }>(Symbol('projectReferences')),
  router: createContext<Router>(Symbol('router')),
  fql: createContext<Query>(Symbol('fql')),

  linkFQL: createContext<Linkifier<string>>(Symbol('link-fql')),
  linkTimeline: createContext<Linkifier<EnvGroup>>(Symbol('link-timeline')),
  linkTest: createContext<Linkifier<WireTypes.TestStats>>(Symbol('link-test')),
}

export class ContextTask<T extends ReadonlyArray<unknown> = ReadonlyArray<unknown>, Element = unknown, R = Linkifier<Element>> extends Task<T, R> {
  private _setContextTask: Task<any, any>;
  constructor(host: ReactiveElement, context: Context<unknown, R|undefined>, task: TaskConfig<T, R>) {
    super(host, task);

    const contextProvider = new ContextProvider(host, { context });
    this._setContextTask = new Task(host, {
      args: () => [this.value],
      task: async ([value], { signal }) => {
        contextProvider.setValue(value);
      },
    })
  }
}

export class TaskEvent<T> extends Event {
  constructor(eventName: string, public status: 'complete'|'error'|'pending', public data?: T) {
    super(eventName, {
      bubbles: true,
      composed: true,
      cancelable: false,
    });
  }
}

export class EventTask<T extends ReadonlyArray<unknown> = ReadonlyArray<unknown>, R = unknown, > extends Task<T, R> {
  constructor(host: ReactiveElement, eventName: string, taskConfig: TaskConfig<T, R>) {
    super(host, {
      ...taskConfig,
      task: async (...args) => {
        host.dispatchEvent(new TaskEvent(eventName, 'pending'));
        try {
          const result = await taskConfig.task.apply(null, args);
          host.dispatchEvent(new TaskEvent(eventName, 'complete', result));
          return result;
        } catch (e) {
          host.dispatchEvent(new TaskEvent(eventName, 'error'));
          throw e; 
        }
      }
    });
  }
}

export class FKProjectChanged extends Event {
  static dispatch(element: Element, project: WireTypes.Project) {
    element.dispatchEvent(new FKProjectChanged({
      orgSlug: project.org.orgSlug,
      projectSlug: project.projectSlug,
    }));
  }

  static readonly eventName = 'fk-project-changed';
  readonly projectSlug: string;
  readonly orgSlug: string;

  constructor(options: { orgSlug: string, projectSlug: string }) {
    super(FKProjectChanged.eventName, {
      bubbles: true,
      composed: true,
      cancelable: false,
    });
    this.orgSlug =options.orgSlug;
    this.projectSlug = options.projectSlug;
  }
}

export class FKDataSnapshotRefresh extends Event {
  static readonly eventName = 'fk-data-snapshot-refresh';

  constructor() {
    super(FKDataSnapshotRefresh.eventName, {
      bubbles: true,
      composed: true,
      cancelable: false,
    });
  }
}

// The signed-in user edited their own profile. The user context is fetched
// once at startup with no arguments, so it has no reason to re-run on its own.
export class FKUserChanged extends Event {
  static readonly eventName = 'fk-user-changed';

  constructor() {
    super(FKUserChanged.eventName, {
      bubbles: true,
      composed: true,
      cancelable: false,
    });
  }
}

export class FKOrgChanged extends Event {
  static readonly eventName = 'fk-org-changed';
  readonly orgSlug: string;

  constructor(options: { orgSlug: string }) {
    super(FKOrgChanged.eventName, {
      bubbles: true,
      composed: true,
      cancelable: false,
    });
    this.orgSlug =options.orgSlug;
  }
}
